<template>
  <div>
    <TableLoader v-if="loader" />
    <v-row v-else no-gutters justify="space-between" class="pb-7">
      <v-col sm="4">
        <CurrentSubscriptionCard
          v-if="subscriptions && subscriptions.product"
          :product="subscriptions && subscriptions.product"
          :canManage="canManage"
          :activeUserCount="activeUserCount"
          :isOrgOnboarded="isOrgOnboarded"
          @openDialog="openOverlay"
          :data-test-id="`${dataTestId}`"
        />
        <v-skeleton-loader class="mt-4" v-else type="image"></v-skeleton-loader>
      </v-col>
      <v-col v-if="isNotTrialPack" :sm="getColSize">
        <NextEstimationCard
          :currentEstimatedBill="currentEstimatedBill"
          :currency="currency"
          :isLeftDrawerPanOpen="isLeftDrawerPanOpen"
        />
      </v-col>
      <v-spacer></v-spacer>
      <v-col class="d-flex justify-end" :sm="5">
        <AddOnsCard
          v-if="isNotTrialPack"
          :isOrgOnboarded="isOrgOnboarded"
          :addOns="addOns"
          :canManage="canManage"
          :available="{
            activeUserCount,
            storage,
            projects
          }"
          @openDialog="openOverlay"
          :data-test-id="`${dataTestId}`"
        />
      </v-col>
    </v-row>
    <div class="mt-4">
      <h4 class="mb-3">Payment Information</h4>
      <PaymentProfiles :canManage="canManage" :currentOrgId="currentOrgId" :data-test-id="`${dataTestId}`"/>
    </div>
    <ChangePlanAndAddOnsDialog
      v-if="showPlansOverlay"
      :addOns="addOns"
      :activeUserCount="activeUserCount"
      :showPlansOverlay="showPlansOverlay"
      @closeOverlay="closeOverlay"
    />
  </div>
</template>
<script>
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import PaymentProfiles from '@/components/Organisations/Billing/PaymentProfiles/PaymentProfiles';
import planDetails from './planDetails';
import { addOns, addOnsConstants } from './planDetails';
import AddOnsCard from './AddOnsCard';
import ChangePlanAndAddOnsDialog from './ChangePlanAndAddOnsDialog';
import NextEstimationCard from './NextEstimationCard';
import TableLoader from '@/components/Common/TableLoader';
import config from '../../../../config';
import regex from '../../../constants/regex';
import { mapState } from 'vuex';
export default {
  data() {
    return {
      addOns: [],
      activeUserCount: 0,
      showPlansOverlay: false,
      loader: true,
      addOnsConstants,
      dataTestId:'configuration__billing-plans__summary'
    };
  },
  props: ['isLeftDrawerPanOpen'],
  components: {
    CurrentSubscriptionCard,
    PaymentProfiles,
    AddOnsCard,
    ChangePlanAndAddOnsDialog,
    NextEstimationCard,
    TableLoader
  },
  mounted() {
    this.getData();
  },
  watch: {
    currentOrgId: function() {
      this.getData();
    }
  },
  computed: {
    ...mapState({
      onboardingStatus: state => state.onboardingStatus,
      currentOrgId: state => state.currentlySelectedOrgId,
      isCurrentOrgLegacy: state => state.isCurrentOrgLegacy
    }),
    isOrgOnboarded() {
      return this.onboardingStatus === 'ONBOARDED';
    },
    currency() {
      return this.subscriptions?.currencySymbol || '';
    },
    currencyName() {
      return this.subscriptions?.currency || '';
    },
    // check for permission can_manage_subscription if selected own org
    // check for permission can_manage_child_subscription if selected child org
    canManage() {
      return this.isOwnOrgSelected()
        ? this.$can('manage', 'subscription')
        : this.$can('manage', 'child_subscription');
    },
    // To find details of current plan by product handle
    currentPlan() {
      let productHandle = this.subscriptions?.product?.handle;
      let plan = (planDetails || []).find(
        plan => plan.planSlug === productHandle
      );
      return plan || {};
    },
    // returns true if pack is not trial
    isNotTrialPack() {
      return (
        this.subscriptions?.product?.handle &&
        this.subscriptions?.product?.handle !== 'trial'
      );
    },
    // To calculate available storage for org by formula :-
    // (perUserStorage * activeUsers) + (no. of data packs * storagePerDataPackInGB)
    storage() {
      const dataPackComponent =
        (this.subscriptions?.component || []).find(
          obj => obj.handle === config?.dataPackHandle
        ) || {};
      return (
        (this.currentPlan?.perUser || 0) * (this.activeUserCount || 0) +
        (dataPackComponent?.quantity || 0) *
          this.addOnsConstants.storagePerDataPackInGB
      );
    },
    // To calculate available projects for org by formula :-
    // (no. of projects comes with plan) + (no. of discovery packs * projectsPerDiscoveryPack)
    projects() {
      const discoveryPackComponent =
        (this.subscriptions?.component || []).find(
          obj => obj.handle === config?.discoveryPackHandle
        ) || {};
      return (
        (this.currentPlan?.projects || 0) +
        (discoveryPackComponent?.quantity || 0) *
          this.addOnsConstants.projectsPerDiscoveryPack
      );
    },
    // To convert estimation bill which comes in cents into upper unit (i.e. dollar or pound or euro)
    currentEstimatedBill() {
      const centsInOneDollar = 100;
      return (
        +this.subscriptions?.subscription?.balanceInCents / centsInOneDollar ||
        0
      );
    },
    // To get column size of next estimation bill card
    getColSize() {
      const oneSixth = 2;
      const oneFourth = 3;
      return this.isLeftDrawerPanOpen ? oneSixth : oneFourth;
    }
  },
  methods: {
    async getData(overlayClosed) {
      try {
        this.loader = true;
        await this.getCurrentOrganisationDetails();
        if (overlayClosed) {
          await this.getUserSubscription('SET_BOTH');
        }
        this.getAddonList();
        // To get active users count if pack is trial
        if (this.subscriptions?.product?.handle === 'trial') {
          this.getUserCount();
        }
      } catch (error) {
        this.snackbar('error', error);
      }
    },
    // open (change plan & add-ons) overlay
    openOverlay() {
      this.showPlansOverlay = true;
    },
    // close (change plan & add-ons) overlay
    closeOverlay(event) {
      this.showPlansOverlay = false;
      if (event === 'confirm') {
        this.getData(true);
      }
    },
    // To get active User account if pack is not trial
    getActiveUserCount() {
      (this.subscriptions?.component || []).map(component => {
        const addOnComponent = (this.addOns || []).find(
          addOn => component?.handle === addOn?.handle
        );
        // To get only user license component
        if (
          !addOnComponent &&
          this.subscriptions?.product?.handle !== 'trial'
        ) {
          this.activeUserCount = component?.quantity || 0;
        }
        // Mapping quantity of allocated components with addOns
        (this.addOns || []).forEach(addOn => {
          if (component?.handle === addOn?.handle) {
            addOn.quantity = component?.quantity || 0;
          }
        });
      });
    },
    // To set description for each add-on pack by converting amount of add-on into selected currency of the org
    setDescription() {
      (this.addOns || []).forEach(addOnComponent => {
        const unitPrice = (addOns || []).find(
          addon => addon?.handle === addOnComponent?.handle
        )?.unitPrice[this.currencyName];
        if (addOnComponent?.description && this.currency && unitPrice) {
          addOnComponent.description = addOnComponent?.description.replace(
            regex.detectAddOnPackAmount,
            `${this.currency}${unitPrice}`
          );
        }
      });
    },
    // To get list of add-on packs
    async getAddonList() {
      try {
        const addonList = await this.$services.billing.componentListing();
        const addonListData = this.parseResponse(addonList);
        this.addOns = addonListData?.[0] || [];
        this.setDescription();
        this.getActiveUserCount();
        this.loader = false;
      } catch (error) {
        this.loader = true;
        this.snackbar('error', error);
      }
    },
    // To get users count
    async getUserCount() {
      try {
        let groupCount = await this.$services.user.getUserInfo('userCounts', {
          orgId: this.currentOrgId
        });
        let data = groupCount?.data?.body?.[0];
        this.activeUserCount = (data?.active ?? 0) - (data?.group ?? 0);
      } catch (err) {
        this.snackbar('error', err);
      }
    }
  }
};
</script>
